import { comboLabel, type Card, type Combo, type GameEvent, type MatchState, type Seat } from "@guandan/engine";
import type { ScheduledSpeech, Snapshot, TableAction, WireCombo, WireEventBody } from "./index.js";
import type { MatchSummary } from "./summary.js";

/**
 * Turn a canonical engine event into what one seat is allowed to see.
 *
 * This is the only place hidden information can leak, so it is a pure function
 * over the canonical event: same event and seat in, same bytes out, every time.
 * Returning `null` drops the event for that seat entirely.
 *
 * Spectators (`seat === null`) get the public projection: no hole cards at all.
 */
export function projectEvent(
  event: GameEvent,
  seat: Seat | null,
  context: {
    levels: readonly [number, number];
    deadlineAt: number | null;
    turnReadyAt: number;
    turnToken: string;
    pendingSpeech?: ScheduledSpeech;
    summary?: MatchSummary;
  },
): WireEventBody | null {
  switch (event.type) {
    case "handStarted":
      return {
        type: "handStarted",
        handNumber: event.handNumber,
        level: event.level,
        playingTeam: event.playingTeam,
        levels: [context.levels[0], context.levels[1]] as const,
      };

    case "dealt":
      // The single most important redaction in the game.
      return {
        type: "dealt",
        hand: seat === null ? [] : [...(event.hands[seat] ?? [])],
        counts: event.hands.map((hand) => hand.length),
      };

    case "tributeResisted":
      return { type: "tributeResisted", seats: [...event.seats] };

    case "tributePaid":
      // Tribute is paid face up.
      return { type: "tributePaid", from: event.from, to: event.to, card: event.card };

    case "tributeReturned":
      // The return is private between the two players involved.
      return {
        type: "tributeReturned",
        from: event.from,
        to: event.to,
        card: seat === event.from || seat === event.to ? event.card : null,
      };

    case "turnChanged":
      return {
        type: "turn",
        seat: event.seat,
        phase: event.phase === "returnTribute" ? "returnTribute" : "playing",
        open: event.open,
        deadlineAt: context.deadlineAt,
        turnReadyAt: context.turnReadyAt,
        turnToken: context.turnToken,
        pendingSpeech: context.pendingSpeech,
      };

    case "played":
      return {
        type: "played",
        seat: event.seat,
        cards: [...event.cards],
        combo: wireCombo(event.combo),
        remaining: event.remaining,
      };

    case "passed":
      return { type: "passed", seat: event.seat };

    case "trickEnded":
      return {
        type: "trickEnded", winner: event.winner, leader: event.leader, jiefeng: event.jiefeng,
      };

    case "playerOut":
      return { type: "playerOut", seat: event.seat, place: event.place };

    case "handEnded":
      return { type: "handEnded", result: event.result };

    case "matchEnded":
      return { type: "matchEnded", winner: event.winner, summary: context.summary };

    default:
      return null;
  }
}

export function wireCombo(combo: Combo): WireCombo {
  return {
    type: combo.type,
    cards: [...combo.cards],
    rank: combo.rank,
    length: combo.length,
    power: combo.power,
    label: comboLabel(combo),
  };
}

/** A cold-start view of the table for one seat. */
export function projectSnapshot(
  state: MatchState,
  seat: Seat | null,
  seq: number,
  deadlineAt: number | null,
  turnReadyAt: number,
  turnToken: string,
  summary?: MatchSummary,
): Snapshot {
  return {
    seq,
    phase: state.phase,
    handNumber: state.handNumber,
    level: state.level,
    playingTeam: state.playingTeam,
    levels: [state.levels[0], state.levels[1]] as const,
    hand: seat === null ? [] : [...((state.hands[seat] ?? []) as Card[])],
    counts: state.hands.map((hand) => hand.length),
    out: [...state.out],
    finished: [...state.finished],
    discard: [...state.discard],
    turn: state.turn,
    open: state.current === null,
    deadlineAt,
    turnReadyAt,
    turnToken,
    current: state.current === null
      ? null
      : { seat: state.current.seat, combo: wireCombo(state.current.combo) },
    summary,
  };
}

/** A canonical event as it is stored: the sequence number and the JSON body. */
export interface StoredEvent {
  readonly seq: number;
  readonly body: string;
}

/** Rebuild the public play/pass log for a cold connection from canonical rows. */
export function projectTableActivity(rows: readonly StoredEvent[]): Pick<
  Snapshot, "trickNumber" | "trickActions" | "lastTrickActions" | "activity"
> {
  let trickNumber = 1;
  let trickActions: TableAction[] = [];
  let lastTrickActions: TableAction[] = [];
  let activity: TableAction[] = [];

  for (const row of rows) {
    const event = JSON.parse(row.body) as GameEvent;
    if (event.type === "handStarted") {
      trickNumber = 1;
      trickActions = [];
      lastTrickActions = [];
      activity = [];
      continue;
    }
    let action: TableAction | null = null;
    if (event.type === "played") {
      action = {
        seq: row.seq,
        trick: trickNumber,
        seat: event.seat,
        kind: "played",
        combo: wireCombo(event.combo),
      };
      if (trickActions.length === 0) lastTrickActions = [];
    } else if (event.type === "passed") {
      action = { seq: row.seq, trick: trickNumber, seat: event.seat, kind: "passed", combo: null };
    } else if (event.type === "trickEnded") {
      lastTrickActions = trickActions;
      trickActions = [];
      trickNumber += 1;
    }
    if (action !== null) {
      trickActions.push(action);
      activity.push(action);
      activity = activity.slice(-120);
    }
  }

  return { trickNumber, trickActions, lastTrickActions, activity };
}
